import TikTokColorIcon from '@components/Icons/TikTokColorIcon';
import { PLATFORMS } from '@lib/utils/platforms';
import { formatBytesToString, formatDurationToString } from '@lib/utils/units';
import type { PostContentSummary, SocialPlatformModule } from '../../types';
import { TikTokComposeFields } from './TikTokComposeFields';

/**
 * TikTok's privacy vocabulary as atlas stores it. New posts are accepted at
 * `PUBLIC_TO_EVERYONE` only (the API refuses the rest at preflight), but rows
 * published before that rule carry `SELF_ONLY`, so every value stays readable.
 */
export type TikTokPrivacy =
  | 'PUBLIC_TO_EVERYONE'
  | 'MUTUAL_FOLLOW_FRIENDS'
  | 'FOLLOWER_OF_CREATOR'
  | 'SELF_ONLY';

/** How each level reads in the form; a level newer than this client shows as itself. */
export const TIKTOK_PRIVACY_LABELS: Record<TikTokPrivacy, string> = {
  PUBLIC_TO_EVERYONE: 'Everyone',
  MUTUAL_FOLLOW_FRIENDS: 'Friends',
  FOLLOWER_OF_CREATOR: 'Followers',
  SELF_ONLY: 'Only me',
};

/** The one level the video publish can send; also what the form offers when the account's settings could not be read. */
const PUBLISHABLE_PRIVACY: TikTokPrivacy = 'PUBLIC_TO_EVERYONE';

/** TikTok's cap on the caption, in UTF-16 code units, which is what `String.length` counts. */
export const TIKTOK_TITLE_MAX_LENGTH = 2200;

/** The account-level interaction switches, as `disabledInteractions` names them. */
export type TikTokInteraction = 'comment' | 'duet' | 'stitch';

/**
 * TikTok's exact post content, as the list returns it and edit takes it back.
 * A draft is `{ platform, draft: true }` and nothing else: the media lands in
 * the account's inbox to finish in the app, so every other field is refused
 * alongside it.
 */
export type TikTokPostContent = {
  platform: 'tiktok';
  draft: boolean;
  title?: string;
  privacy?: TikTokPrivacy;
  disableComment?: boolean;
  disableDuet?: boolean;
  disableStitch?: boolean;
  aiGenerated?: boolean;
  paidPartnership?: boolean;
  promotesOwnBrand?: boolean;
};

/**
 * The account's settings as TikTok answers them. `privacyLevels` and
 * `disabledInteractions` are in TikTok's vocabulary (a value newer than this
 * client passes through); `limits` is the account's own cap or the platform
 * ceiling, whichever is lower.
 */
export type TikTokAccountCapabilities = {
  platform: 'tiktok';
  available: true;
  displayName: string;
  /** Empty means the account cannot publish through the API; an inbox draft still can. */
  privacyLevels: string[];
  disabledInteractions: string[];
  limits: { minDurationSeconds: number; maxDurationSeconds: number; maxSizeBytes: number };
};

const describeContent = (content: TikTokPostContent): PostContentSummary => {
  if (content.draft) {
    return { headline: 'Send to TikTok inbox', privacy: null, draft: true, variant: null };
  }
  return {
    headline: content.title || 'Untitled post',
    // Anything short of everyone reads as private: the safe side if the vocabulary widens.
    privacy: !content.privacy
      ? null
      : content.privacy === 'PUBLIC_TO_EVERYONE'
        ? 'public'
        : 'private',
    draft: false,
    variant: null,
  };
};

const parseContent = (raw: unknown): TikTokPostContent => {
  const content = raw as Partial<TikTokPostContent> | null;
  if (content?.platform !== 'tiktok') {
    throw new Error(`tiktok post with content for ${String(content?.platform)}`);
  }
  return { ...content, platform: 'tiktok', draft: content.draft === true };
};

/**
 * A publishable post with every switch off and no privacy level: the contract
 * says to offer exactly the account's levels with nothing preselected, so the
 * form lists the level as missing until the user picks one.
 */
const defaultContent = (): TikTokPostContent => ({
  platform: 'tiktok',
  draft: false,
  disableComment: false,
  disableDuet: false,
  disableStitch: false,
  aiGenerated: false,
  paidPartnership: false,
  promotesOwnBrand: false,
});

/** The levels the form offers: the account's, or the publishable one when its settings could not be read. */
export const offeredPrivacyLevels = (capabilities: TikTokAccountCapabilities | null): string[] =>
  capabilities ? capabilities.privacyLevels : [PUBLISHABLE_PRIVACY];

/**
 * Whether the account may publish through the API at all: its settings list
 * the one publishable level. Unknown settings answer yes, for the API as a
 * whole. No leaves only the inbox; the fields and the submit gate both say so.
 */
export const canPublish = (capabilities: TikTokAccountCapabilities | null): boolean =>
  offeredPrivacyLevels(capabilities).includes(PUBLISHABLE_PRIVACY);

const missingField = (
  content: TikTokPostContent,
  capabilities: TikTokAccountCapabilities | null,
): string | null => {
  if (content.draft) return null;
  if (!canPublish(capabilities)) return 'This account can only receive posts in its inbox.';
  if (!content.privacy) return 'Choose who can see this post.';
  if ((content.title ?? '').length > TIKTOK_TITLE_MAX_LENGTH) {
    return `The caption is over ${TIKTOK_TITLE_MAX_LENGTH.toLocaleString()} characters.`;
  }
  return null;
};

/** Fields drift by lifecycle stage on backend objects, hence the optionals. */
type TikTokAccountCapabilitiesWire = {
  platform?: string;
  displayName?: string;
  privacyLevels?: string[];
  disabledInteractions?: string[];
  limits?: { minDurationSeconds?: number; maxDurationSeconds?: number; maxSizeBytes?: number };
};

const parseCapabilities = (raw: unknown): TikTokAccountCapabilities => {
  const wire = raw as TikTokAccountCapabilitiesWire | null;
  if (wire?.platform !== 'tiktok') {
    throw new Error(`tiktok account with capabilities for ${String(wire?.platform)}`);
  }
  return {
    platform: 'tiktok',
    available: true,
    displayName: wire.displayName ?? '',
    privacyLevels: wire.privacyLevels ?? [],
    disabledInteractions: wire.disabledInteractions ?? [],
    limits: {
      minDurationSeconds: wire.limits?.minDurationSeconds ?? 0,
      maxDurationSeconds: wire.limits?.maxDurationSeconds ?? Infinity,
      maxSizeBytes: wire.limits?.maxSizeBytes ?? Infinity,
    },
  };
};

const describeCapabilities = ({ limits }: TikTokAccountCapabilities): string =>
  `Media up to ${formatDurationToString(limits.maxDurationSeconds)} and ${formatBytesToString(limits.maxSizeBytes)}`;

export const tiktokModule: SocialPlatformModule<TikTokPostContent, TikTokAccountCapabilities> = {
  platform: 'tiktok',
  label: PLATFORMS.tiktok.displayName,
  icon: <TikTokColorIcon />,
  colors: { bg: 'rgba(37, 244, 238, 0.14)', text: '#8ff5f1' },
  accountNoun: 'account',
  disconnectNote: 'StarZero’s access to the account is withdrawn on TikTok as well.',
  // A TikTok login is its one account, so the API has no occasion to answer this; worded all the same.
  noAccountsNote:
    'Your TikTok login shared no account that StarZero can post to. Connect again, allowing everything asked for.',
  parseContent,
  describeContent,
  defaultContent,
  missingField,
  parseCapabilities,
  describeCapabilities,
  ComposeFields: TikTokComposeFields,
};
